import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { ContactSearchMethods, SearchObjective, SearchToAdd, SelectorIds, SingleSearch } from '../../../../campaign-builder.types';
import EnterSearchDetails from './enter-search-details';
import SelectSearchObjective from './select-search-objective';

interface Props {
  productGroup: ProductGroups;
  contactSearchMethod: ContactSearchMethods | '';
  selectedContactSearchObjective: SearchObjective | null;
  searchToAdd: SearchToAdd | null;
  linkedinAccounts: LinkedinAccount[];
  separateLinkedinAccount: string;
  addSearchesLoading: boolean;

  handleSelectContactSearchObjective(newObjective: SearchObjective): () => void;

  handleOwnSearchFieldChange(fieldType: keyof SingleSearch): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handlePeopleTypeChange?: (type: 'includeReactions' | 'includeComments' | 'includeAuthor') => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleProfileDegreeChange?: (value: 1 | 2 | 3) => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSeparateLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAddOwnLinkedinSearch(): void;

  handleAddOwnLinkedinSearchWithSkipQualification(): void;

  handleJobTitlesChange?: (titles: string[]) => void;
}

const OwnContactSearch: React.FC<Props> = ({
  productGroup,
  contactSearchMethod,
  selectedContactSearchObjective,
  searchToAdd,
  linkedinAccounts,
  separateLinkedinAccount,
  addSearchesLoading,
  handleSelectContactSearchObjective,
  handleOwnSearchFieldChange,
  handlePeopleTypeChange,
  handleProfileDegreeChange,
  handleSeparateLinkedinAccountChange,
  handleAddOwnLinkedinSearch,
  handleAddOwnLinkedinSearchWithSkipQualification,
  handleJobTitlesChange,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const lastSectionRef = useRef<HTMLDivElement>(null);

  const isSearchMethodSelected = !!contactSearchMethod;
  const isSearchObjectiveSelected = isSearchMethodSelected && !_.isNil(selectedContactSearchObjective);

  const isJobPostings = selectedContactSearchObjective?.key === 'jobPostings';
  const jobTitles = searchToAdd?.search.targetJobTitles || [];

  // Update validation to include job titles for jobPostings type
  const disabled =
    !isSearchMethodSelected ||
    !isSearchObjectiveSelected ||
    !searchToAdd?.search.name ||
    !searchToAdd?.search.url ||
    (isJobPostings && (!jobTitles.length || jobTitles.length === 0));

  // Determine which LinkedIn account to use
  const selectedLinkedinAccount = separateLinkedinAccount ? linkedinAccounts.find((acc) => acc._id === separateLinkedinAccount) : linkedinAccounts[0];

  useEffect(() => {
    if ((isSearchMethodSelected || selectedContactSearchObjective) && lastSectionRef.current) {
      lastSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isSearchMethodSelected, selectedContactSearchObjective]);

  return (
    <>
      <section id={SelectorIds.CRAWLING_ACCOUNT} className={cx(classes.section)}>
        <Typography className={cx(classes.subTitle, 'dense')} mb={'8px'}>
          {t('selectLinkedInAccountForSourcing')}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {t('useDifferentLinkedInAccountsForSourcing')}
        </Typography>
        <PaiSelect value={separateLinkedinAccount} onChange={handleSeparateLinkedinAccountChange} style={{ maxWidth: '350px' }}>
          <PaiMenuItem value={''}>{t('none')}</PaiMenuItem>
          {linkedinAccounts.map((account) => (
            <PaiMenuItem key={account._id} value={account._id}>
              {account.userEmail}
            </PaiMenuItem>
          ))}
        </PaiSelect>
      </section>
      <section id={SelectorIds.OWN_SEARCH_OBJECTIVE} className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, !isSearchObjectiveSelected ? 'active' : undefined)} />
        <Typography className={classes.subTitle} mb={'8px'}>
          {t('selectContactSearchObjective')}
        </Typography>
        <Typography className={classes.greyText} mb={'16px'}>
          {t('customizeSearchSelectOptions')}
        </Typography>
        <SelectSearchObjective
          selectedContactSearchObjective={selectedContactSearchObjective}
          handleSelectContactSearchObjective={handleSelectContactSearchObjective}
          selectedLinkedinAccount={selectedLinkedinAccount}
        />
      </section>
      {isSearchObjectiveSelected && !_.isNil(searchToAdd) ? (
        <section id={SelectorIds.OWN_SEARCH_OBJECTIVE} className={classes.section}>
          <ArrowRightAltIcon className={cx(classes.arrow, isSearchObjectiveSelected ? 'active' : undefined)} />
          <Typography className={classes.subTitle} mb={'8px'}>
            {t('yourSelectedSearches')}
          </Typography>
          <Typography className={classes.greyText} mb={'16px'}>
            {t('kickOffLinkedinSourcing')}
          </Typography>
          <EnterSearchDetails
            productGroup={productGroup}
            searchToAdd={searchToAdd}
            isSaveDisabled={disabled}
            addSearchesLoading={addSearchesLoading}
            selectedContactSearchObjective={selectedContactSearchObjective}
            handleAddOwnLinkedinSearch={handleAddOwnLinkedinSearch}
            handleAddOwnLinkedinSearchWithSkipQualification={handleAddOwnLinkedinSearchWithSkipQualification}
            handleOwnSearchFieldChange={handleOwnSearchFieldChange}
            handlePeopleTypeChange={handlePeopleTypeChange}
            handleProfileDegreeChange={handleProfileDegreeChange}
            handleJobTitlesChange={handleJobTitlesChange}
          />
        </section>
      ) : null}
      <div ref={lastSectionRef} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  section: {
    position: 'relative',
    padding: '24px 48px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    '&.border-bottom-disabled': {
      borderBottom: 'none',
    },
  },
  arrow: {
    display: 'none',
    position: 'absolute',
    top: '18px',
    left: '0px',
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
    '&.active': {
      display: 'inline-block',
    },
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default OwnContactSearch;
